/**
 * TanStack Query Client Configuration
 *
 * Centralized configuration for React Query with caching policies
 * optimized for dashboard data.
 *
 * Caching Strategy:
 * - KPIs: 5 minute staleTime (less volatile, aggregated metrics)
 * - Inventory Quality: 1 minute staleTime (more dynamic)
 * - Store Financials: 1 minute staleTime (transaction-driven data)
 *
 * @module services/queryClient
 */

import { QueryClient } from '@tanstack/react-query';
import type { ImportTargetTable } from './csvImport';

/**
 * Query key factory for consistent cache key management
 */
export const queryKeys = {
  // Store-level data
  kpis: (tenantId: string, storeId: string) => ['kpis', tenantId, storeId] as const,
  inventoryQuality: (storeId: string) => ['inventoryQuality', storeId] as const,
  storeFinancials: (storeId: string) => ['storeFinancials', storeId] as const,
  extendedFinancials: (tenantId: string, storeId: string) => ['extendedFinancials', tenantId, storeId] as const,
  operationalCompliance: (tenantId: string, storeId: string) => ['operationalCompliance', tenantId, storeId] as const,
  inventoryInsights: (storeId: string) => ['inventoryInsights', storeId] as const,
  storeZones: (storeId: string) => ['storeZones', storeId] as const,
  processErrors: (tenantId: string, storeId: string) => ['processErrors', tenantId, storeId] as const,
  zoneMetrics: (tenantId: string, storeId: string, zoneId: string) => ['zoneMetrics', tenantId, storeId, zoneId] as const,
  riskTrend: (storeId: string, days: number) => ['riskTrend', storeId, days] as const,
  operationalKPIs: (tenantId: string, storeId: string) => ['operationalKPIs', tenantId, storeId] as const,

  // Alerts, cases, tasks - SECURITY: tenantId required for tenant isolation
  alerts: (tenantId: string, storeId?: string) => storeId ? ['alerts', tenantId, storeId] as const : ['alerts', tenantId] as const,
  riskPatterns: (storeId: string) => ['riskPatterns', storeId] as const,
  cases: (tenantId: string, storeId?: string) => storeId ? ['cases', tenantId, storeId] as const : ['cases', tenantId] as const,
  tasks: (tenantId: string, storeId?: string) => storeId ? ['tasks', tenantId, storeId] as const : ['tasks', tenantId] as const,

  // Global data - SECURITY: tenantId required to prevent cross-tenant data leakage
  globalOverview: (tenantId?: string) => tenantId ? ['globalOverview', tenantId] as const : ['globalOverview'] as const,
  storeStatuses: (tenantId: string) => ['storeStatuses', tenantId] as const,
  tenantUsers: (tenantId: string) => ['tenantUsers', tenantId] as const,
  kpiDetails: (kpiId: string, storeId: string) => ['kpiDetails', kpiId, storeId] as const,
  dataFreshness: (tenantId: string, storeId: string) => ['dataFreshness', tenantId, storeId] as const,

  // Data quality monitoring
  dataQualityRules: (tenantId: string) => ['dataQualityRules', tenantId] as const,
  dataQualityAlerts: (tenantId: string, storeId?: string) => storeId ? ['dataQualityAlerts', tenantId, storeId] as const : ['dataQualityAlerts', tenantId] as const,
  dataQualityTriggers: (tenantId: string) => ['dataQualityTriggers', tenantId] as const,

  // Risk & Distortion Intelligence (v1.8.0)
  riskIndices: (tenantId: string, storeId: string) => ['riskIndices', tenantId, storeId] as const,
  paretoAnalysis: (tenantId: string, storeId: string) => ['paretoAnalysis', tenantId, storeId] as const,
  paretoConcentration: (tenantId: string, storeId: string) => ['paretoConcentration', tenantId, storeId] as const,
  complianceOutliers: (tenantId: string, storeId: string) => ['complianceOutliers', tenantId, storeId] as const,
  phantomRevenue: (tenantId: string, storeId: string) => ['phantomRevenue', tenantId, storeId] as const,
  tenantRiskSummary: (tenantId: string) => ['tenantRiskSummary', tenantId] as const,
};

/**
 * Stale time constants in milliseconds
 *
 * CQRS-Lite Frontend Caching Strategy (v3.3.0):
 * - ANALYTICAL data (pre-computed KPIs, trends, GMROI): 5 minute staleTime
 * - OPERATIONAL data (alerts, tasks): 30 second - 1 minute staleTime
 *
 * This reduces client-side compute fatigue by:
 * 1. Leveraging pre-computed materialized views (view_dashboard_kpis)
 * 2. Minimizing refetches for slow-changing aggregated metrics
 * 3. Keeping real-time responsiveness only where needed (alerts)
 */
export const STALE_TIMES = {
  // ========== ANALYTICAL DATA (5 minute staleTime) ==========
  // These use pre-computed materialized views - data changes slowly
  KPI: 5 * 60 * 1000,                      // 5 minutes - pre-computed in view_dashboard_kpis
  INVENTORY_QUALITY: 5 * 60 * 1000,        // 5 minutes - aggregated quality metrics
  STORE_FINANCIALS: 5 * 60 * 1000,         // 5 minutes - financial summaries from daily_store_stats
  EXTENDED_FINANCIALS: 5 * 60 * 1000,      // 5 minutes - GMROI, shrinkage trends
  INVENTORY_INSIGHTS: 5 * 60 * 1000,       // 5 minutes - shrinkage analysis
  STORE_STATUSES: 5 * 60 * 1000,           // 5 minutes - store overview data
  RISK_TREND: 5 * 60 * 1000,               // 5 minutes - historical risk trends
  KPI_DETAILS: 5 * 60 * 1000,              // 5 minutes - drill-down data
  GLOBAL_OVERVIEW: 5 * 60 * 1000,          // 5 minutes - chain-wide aggregates

  // ========== OPERATIONAL DATA (30s - 1 minute staleTime) ==========
  // These require near-real-time updates for operational responsiveness
  ALERTS: 30 * 1000,                       // 30 seconds - time-sensitive security data
  CASES: 60 * 1000,                        // 1 minute - active investigations
  TASKS: 60 * 1000,                        // 1 minute - operational tasks
  OPERATIONAL_COMPLIANCE: 60 * 1000,       // 1 minute - task completion status

  // ========== DEFAULT ==========
  DEFAULT: 5 * 60 * 1000,                  // 5 minutes - prefer longer cache for analytics
} as const;

/**
 * Create a configured QueryClient instance
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Default stale time for most queries
        staleTime: STALE_TIMES.DEFAULT,
        // Keep data in cache for 10 minutes even if unused
        gcTime: 10 * 60 * 1000,
        // Don't refetch on window focus for dashboard (prevents jarring updates)
        refetchOnWindowFocus: false,
        // Retry failed queries up to 2 times
        retry: 2,
        // Delay between retries
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      },
    },
  });
}

// Singleton query client for the application
export const queryClient = createQueryClient();


/**
 * Invalidate relevant caches after CSV import completes.
 * Each import type affects different data queries.
 *
 * @param targetTable - The table that was imported
 */
export function invalidateCacheAfterImport(targetTable: ImportTargetTable): void {
  switch (targetTable) {
    case 'stores':
      // Stores affect global overview and store statuses
      queryClient.invalidateQueries({ queryKey: ['storeStatuses'] });
      queryClient.invalidateQueries({ queryKey: ['globalOverview'] });
      queryClient.invalidateQueries({ queryKey: ['fkReferences', 'store_name'] });
      break;

    case 'products':
      // Products affect inventory quality and financial metrics
      queryClient.invalidateQueries({ queryKey: ['inventoryQuality'] });
      queryClient.invalidateQueries({ queryKey: ['inventoryInsights'] });
      queryClient.invalidateQueries({ queryKey: ['storeFinancials'] });
      queryClient.invalidateQueries({ queryKey: ['extendedFinancials'] });
      queryClient.invalidateQueries({ queryKey: ['fkReferences', 'sku'] });
      break;

    case 'inventory':
      // Inventory affects KPIs, quality metrics, and insights
      queryClient.invalidateQueries({ queryKey: ['kpis'] });
      queryClient.invalidateQueries({ queryKey: ['inventoryQuality'] });
      queryClient.invalidateQueries({ queryKey: ['inventoryInsights'] });
      queryClient.invalidateQueries({ queryKey: ['storeFinancials'] });
      queryClient.invalidateQueries({ queryKey: ['extendedFinancials'] });
      queryClient.invalidateQueries({ queryKey: ['kpiDetails'] });
      queryClient.invalidateQueries({ queryKey: ['processErrors'] });
      break;

    case 'transactions':
      // Transactions affect financials, KPIs, and shrinkage data
      queryClient.invalidateQueries({ queryKey: ['kpis'] });
      queryClient.invalidateQueries({ queryKey: ['storeFinancials'] });
      queryClient.invalidateQueries({ queryKey: ['extendedFinancials'] });
      queryClient.invalidateQueries({ queryKey: ['kpiDetails'] });
      queryClient.invalidateQueries({ queryKey: ['globalOverview'] });
      break;

    case 'suppliers':
      // Suppliers affect inventory and financials + FK autocomplete cache
      queryClient.invalidateQueries({ queryKey: ['inventoryQuality'] });
      queryClient.invalidateQueries({ queryKey: ['inventoryInsights'] });
      queryClient.invalidateQueries({ queryKey: ['storeFinancials'] });
      queryClient.invalidateQueries({ queryKey: ['fkReferences', 'supplier_code'] });
      break;

    case 'purchase_orders':
    case 'receiving_logs':
      // Supply chain imports affect inventory and financials
      queryClient.invalidateQueries({ queryKey: ['inventoryQuality'] });
      queryClient.invalidateQueries({ queryKey: ['inventoryInsights'] });
      queryClient.invalidateQueries({ queryKey: ['storeFinancials'] });
      // Supply chain dashboard queries
      queryClient.invalidateQueries({ queryKey: ['pipeline-stock'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-summary'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-agreements'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-fulfillment'] });
      queryClient.invalidateQueries({ queryKey: ['agreement-compliance'] });
      queryClient.invalidateQueries({ queryKey: ['po-stats'] });
      break;

    case 'historical_inventory_snapshots':
      // Historical snapshots affect IRA trending and inventory accuracy
      queryClient.invalidateQueries({ queryKey: ['inventoryQuality'] });
      queryClient.invalidateQueries({ queryKey: ['kpiDetails'] });
      queryClient.invalidateQueries({ queryKey: ['iraHistory'] });
      queryClient.invalidateQueries({ queryKey: ['kpiTrendHistory'] });
      // Risk distortion analysis depends on inventory data
      queryClient.invalidateQueries({ queryKey: ['riskIndices'] });
      queryClient.invalidateQueries({ queryKey: ['paretoAnalysis'] });
      queryClient.invalidateQueries({ queryKey: ['complianceOutliers'] });
      queryClient.invalidateQueries({ queryKey: ['phantomRevenue'] });
      queryClient.invalidateQueries({ queryKey: ['tenantRiskSummary'] });
      break;

    case 'employees':
      // Employees affect cashier risk analysis and operational metrics
      queryClient.invalidateQueries({ queryKey: ['kpis'] });
      queryClient.invalidateQueries({ queryKey: ['storeFinancials'] });
      break;

    default:
      // Hierarchy, stats, quality, transfers, line items, discounts/returns…:
      // these feed many derived views — refetch everything that is mounted.
      queryClient.invalidateQueries();
      return;
  }

  // Cross-store aggregates and pickers read most imported tables
  for (const key of ['tenant-aggregates', 'store-leaderboard', 'store-names', 'global-filter-options', 'storeTransfers']) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }

  // Always refetch alerts and global overview since imports may create new alerts
  queryClient.invalidateQueries({ queryKey: ['alerts'] });
}
